/**
 * FAQ element design presets + scoped CSS for builder canvas and region runtime.
 * Authors pick a template in the inspector; appearance must match published output.
 */

import type {
	ElementStyles,
	FaqDesignPreset,
	FaqDesignSettings,
} from "../ui/types";
import { pbNodeBeatThemeDescendantSelector } from "./elementTypographyOverridesCss";

export interface ResolvedFaqDesign {
	/** Explicit answer font-size override (beats Theme Manager body / text rules). */
	answerFontSize?: string;
	preset: FaqDesignPreset;
	/** Explicit question font-size override (beats Theme Manager `h*` rules). */
	questionFontSize?: string;
}

export type FaqPresetStyleBundle = {
	defaultAnswerStyles: ElementStyles;
	defaultItemStyles: ElementStyles;
	defaultQuestionStyles: ElementStyles;
	iconPosition: "left" | "right";
	itemGap: string;
	triggerIcon: "chevron" | "plus_minus" | "none";
};

const PRESETS: readonly FaqDesignPreset[] = [
	"outlined",
	"divided",
	"cards",
	"soft",
	"accent",
] as const;

export function isFaqDesignPreset(v: unknown): v is FaqDesignPreset {
	return typeof v === "string" && (PRESETS as readonly string[]).includes(v);
}

export function parseFaqDesignJson(
	raw: string | undefined
): FaqDesignSettings | undefined {
	if (!raw?.trim()) {
		return undefined;
	}
	try {
		const v = JSON.parse(raw) as unknown;
		if (typeof v !== "object" || v === null || Array.isArray(v)) {
			return undefined;
		}
		return v as FaqDesignSettings;
	} catch {
		return undefined;
	}
}

/** The preset an FAQ actually renders with — "outlined" when unset or invalid. */
export function resolveFaqDesignPreset(
	settings?: FaqDesignSettings | null
): FaqDesignPreset {
	return isFaqDesignPreset(settings?.preset) ? settings.preset : "outlined";
}

export function resolveFaqDesign(
	settings?: FaqDesignSettings | null,
	fallbackSizes?: { answerFontSize?: string; questionFontSize?: string }
): ResolvedFaqDesign {
	const preset = resolveFaqDesignPreset(settings);
	const q =
		(typeof settings?.questionFontSize === "string" &&
		settings.questionFontSize.trim()
			? settings.questionFontSize.trim()
			: undefined) ??
		(typeof fallbackSizes?.questionFontSize === "string" &&
		fallbackSizes.questionFontSize.trim()
			? fallbackSizes.questionFontSize.trim()
			: undefined);
	const a =
		(typeof settings?.answerFontSize === "string" &&
		settings.answerFontSize.trim()
			? settings.answerFontSize.trim()
			: undefined) ??
		(typeof fallbackSizes?.answerFontSize === "string" &&
		fallbackSizes.answerFontSize.trim()
			? fallbackSizes.answerFontSize.trim()
			: undefined);
	return {
		preset,
		questionFontSize: q,
		answerFontSize: a,
	};
}

/** Safe CSS class suffix from builder node id. */
export function faqDesignScopeId(nodeId: string): string {
	const safe = nodeId.replace(/[^a-zA-Z0-9_-]/g, "");
	return safe.length > 0 ? safe : "faq";
}

export function faqDesignClassNames(
	nodeId: string,
	design: ResolvedFaqDesign,
	extraClass?: string
): string {
	const parts = [
		"pb-faq",
		`pb-faq-${faqDesignScopeId(nodeId)}`,
		`pb-faq-preset-${design.preset}`,
	];
	if (extraClass?.trim()) {
		parts.push(extraClass.trim());
	}
	return parts.filter(Boolean).join(" ");
}

/**
 * Template style bundles applied when the author picks a design.
 * Inline defaults keep the canvas WYSIWYG; scoped CSS adds animation / polish.
 */
export const FAQ_PRESET_STYLE_BUNDLES: Record<
	FaqDesignPreset,
	FaqPresetStyleBundle
> = {
	outlined: {
		itemGap: "10px",
		triggerIcon: "chevron",
		iconPosition: "right",
		defaultItemStyles: {
			borderWidth: "1px",
			borderStyle: "solid",
			borderColor:
				"color-mix(in srgb, var(--tt-color-fg, #0f172a) 12%, transparent)",
			borderRadius: "12px",
			overflow: "hidden",
			backgroundColor: "var(--tt-color-bg, #ffffff)",
		},
		defaultQuestionStyles: {
			color: "var(--tt-color-fg, #0f172a)",
			padding: "16px 18px",
			margin: "0",
			width: "100%",
			fontWeight: "600",
			lineHeight: "1.35",
		},
		defaultAnswerStyles: {
			color: "var(--tt-color-fg, #0f172a)",
			lineHeight: "1.65",
			padding: "0 18px 18px 18px",
			margin: "0",
			opacity: "0.88",
		},
	},
	divided: {
		itemGap: "0",
		triggerIcon: "plus_minus",
		iconPosition: "right",
		defaultItemStyles: {
			borderWidth: "0 0 1px 0",
			borderStyle: "solid",
			borderColor:
				"color-mix(in srgb, var(--tt-color-fg, #0f172a) 12%, transparent)",
			borderRadius: "0",
			overflow: "visible",
			backgroundColor: "transparent",
		},
		defaultQuestionStyles: {
			color: "var(--tt-color-fg, #0f172a)",
			padding: "18px 4px",
			margin: "0",
			width: "100%",
			fontWeight: "600",
			lineHeight: "1.35",
		},
		defaultAnswerStyles: {
			color: "var(--tt-color-fg, #0f172a)",
			lineHeight: "1.65",
			padding: "0 4px 18px 4px",
			margin: "0",
			opacity: "0.88",
		},
	},
	cards: {
		itemGap: "14px",
		triggerIcon: "chevron",
		iconPosition: "right",
		defaultItemStyles: {
			borderWidth: "1px",
			borderStyle: "solid",
			borderColor:
				"color-mix(in srgb, var(--tt-color-fg, #0f172a) 6%, transparent)",
			borderRadius: "16px",
			overflow: "hidden",
			backgroundColor: "var(--tt-color-bg, #ffffff)",
			boxShadow:
				"0 4px 18px color-mix(in srgb, var(--tt-color-fg, #0f172a) 8%, transparent)",
		},
		defaultQuestionStyles: {
			color: "var(--tt-color-fg, #0f172a)",
			padding: "18px 20px",
			margin: "0",
			width: "100%",
			fontWeight: "600",
			lineHeight: "1.35",
		},
		defaultAnswerStyles: {
			color: "var(--tt-color-fg, #0f172a)",
			lineHeight: "1.65",
			padding: "0 20px 20px 20px",
			margin: "0",
			opacity: "0.9",
		},
	},
	soft: {
		itemGap: "8px",
		triggerIcon: "plus_minus",
		iconPosition: "right",
		defaultItemStyles: {
			borderWidth: "0",
			borderStyle: "none",
			borderColor: "transparent",
			borderRadius: "14px",
			overflow: "hidden",
			backgroundColor:
				"color-mix(in srgb, var(--tt-color-muted, #f1f5f9) 85%, var(--tt-color-bg, #fff))",
		},
		defaultQuestionStyles: {
			color: "var(--tt-color-fg, #0f172a)",
			padding: "16px 18px",
			margin: "0",
			width: "100%",
			fontWeight: "600",
			lineHeight: "1.35",
		},
		defaultAnswerStyles: {
			color: "var(--tt-color-fg, #0f172a)",
			lineHeight: "1.65",
			padding: "0 18px 16px 18px",
			margin: "0",
			opacity: "0.88",
		},
	},
	accent: {
		itemGap: "12px",
		triggerIcon: "chevron",
		iconPosition: "left",
		defaultItemStyles: {
			borderWidth: "0 0 0 3px",
			borderStyle: "solid",
			borderColor: "var(--tt-color-primary, #0ea5e9)",
			borderRadius: "0 12px 12px 0",
			overflow: "hidden",
			backgroundColor:
				"color-mix(in srgb, var(--tt-color-primary, #0ea5e9) 6%, var(--tt-color-bg, #fff))",
		},
		defaultQuestionStyles: {
			color: "var(--tt-color-fg, #0f172a)",
			padding: "16px 18px",
			margin: "0",
			width: "100%",
			fontWeight: "600",
			lineHeight: "1.35",
		},
		defaultAnswerStyles: {
			color: "var(--tt-color-fg, #0f172a)",
			lineHeight: "1.65",
			padding: "0 18px 18px 18px",
			margin: "0",
			opacity: "0.9",
		},
	},
};

export const FAQ_DESIGN_PRESET_OPTIONS: {
	value: FaqDesignPreset;
	label: string;
	hint: string;
}[] = [
	{
		value: "outlined",
		label: "Outlined",
		hint: "Rounded bordered panels — clean default",
	},
	{
		value: "divided",
		label: "Divided",
		hint: "Minimal list with hairline separators",
	},
	{
		value: "cards",
		label: "Cards",
		hint: "Elevated cards with soft shadow",
	},
	{
		value: "soft",
		label: "Soft fill",
		hint: "Muted background chips, no border",
	},
	{
		value: "accent",
		label: "Accent bar",
		hint: "Primary left edge + tinted panel",
	},
];

function sanitizeCssLength(raw: string | undefined): string | undefined {
	if (!raw?.trim()) {
		return undefined;
	}
	const v = raw.trim();
	// Allow common length / clamp / var() values; reject `;` / braces to avoid injection.
	if (/[;{}]|\/\*|\*\//.test(v)) {
		return undefined;
	}
	return v;
}

/**
 * Scoped CSS: preset polish + question/answer font-size with `!important`
 * so Theme Manager `h3` / body rules cannot win over author sizes.
 *
 * Font-size rules use {@link pbNodeBeatThemeDescendantSelector} — a plain
 * `.pb-faq-* h3` loses to `.pb-builder-theme-scope h3:not(...)` when both use
 * `!important`.
 */
export function buildFaqDesignScopedCss(
	nodeId: string,
	design: ResolvedFaqDesign
): string {
	const scope = `.pb-faq-${faqDesignScopeId(nodeId)}`;
	const qSize = sanitizeCssLength(design.questionFontSize);
	const aSize = sanitizeCssLength(design.answerFontSize);

	const lines: string[] = [
		`${scope}{--pb-faq-ease:cubic-bezier(0.22,1,0.36,1);--pb-faq-dur:0.32s;}`,
		/* Site typography: answers are plain divs (not `p`), so UA default is often Times —
		   pin body copy to Theme Manager Sans; questions keep Theme Manager `h3` rules. */
		`${scope} .pb-faq-answer-body{font-family:var(--tt-font-sans,system-ui,sans-serif);}`,
		`${scope} details{transition:border-color var(--pb-faq-dur) var(--pb-faq-ease),box-shadow var(--pb-faq-dur) var(--pb-faq-ease),background-color var(--pb-faq-dur) var(--pb-faq-ease);}`,
		`${scope} details > summary{list-style:none;transition:background-color 0.2s var(--pb-faq-ease),color 0.2s var(--pb-faq-ease);}`,
		`${scope} details > summary::-webkit-details-marker{display:none;}`,
		`${scope} details > summary:focus{outline:none;}`,
		`${scope} details > summary:focus-visible{outline:2px solid color-mix(in srgb,var(--tt-color-primary,#0ea5e9) 70%,transparent);outline-offset:2px;border-radius:8px;}`,
		`${scope} details > summary:hover{background-color:color-mix(in srgb,var(--tt-color-fg,#0f172a) 3.5%,transparent);}`,
		`${scope} .pb-faq-icon{display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;width:1.5rem;height:1.5rem;border-radius:9999px;transition:transform var(--pb-faq-dur) var(--pb-faq-ease),background-color 0.2s ease,color 0.2s ease,opacity 0.2s ease;opacity:0.72;}`,
		`${scope} .pb-faq-search{width:100%;padding:0.55rem 0.75rem;margin-bottom:0.5rem;border:1px solid color-mix(in srgb,var(--tt-color-fg,#0f172a) 18%,transparent);border-radius:8px;background:transparent;color:inherit;font-family:var(--tt-font-sans,system-ui,sans-serif);font-size:0.95em;}`,
		`${scope} .pb-faq-search:focus-visible{outline:2px solid color-mix(in srgb,var(--tt-color-primary,#0ea5e9) 70%,transparent);outline-offset:1px;}`,
		`${scope} .pb-faq-categories{display:flex;flex-wrap:wrap;gap:0.4rem;margin-bottom:0.65rem;}`,
		`${scope} .pb-faq-category{padding:0.3rem 0.7rem;border-radius:9999px;border:1px solid color-mix(in srgb,var(--tt-color-fg,#0f172a) 18%,transparent);background:transparent;color:inherit;font-family:var(--tt-font-sans,system-ui,sans-serif);font-size:0.85em;cursor:pointer;transition:background-color 0.2s ease,border-color 0.2s ease;}`,
		`${scope} .pb-faq-category:hover{background-color:color-mix(in srgb,var(--tt-color-fg,#0f172a) 6%,transparent);}`,
		`${scope} .pb-faq-category[data-active="true"]{background-color:var(--tt-color-primary,#0ea5e9);border-color:var(--tt-color-primary,#0ea5e9);color:#fff;}`,
		`${scope} .pb-faq-item-icon{display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;width:1.25rem;height:1.25rem;margin-right:0.5rem;}`,
		`${scope} .pb-faq-item-icon svg{width:100%;height:100%;}`,
		`${scope} .pb-faq-no-results{font-family:var(--tt-font-sans,system-ui,sans-serif);opacity:0.7;padding:0.5rem 0;}`,
		`${scope} details[open] > summary .pb-faq-icon{opacity:1;}`,
		`${scope} .pb-faq-chevron{display:inline-block;transition:transform var(--pb-faq-dur) var(--pb-faq-ease);transform-origin:center;line-height:1;font-size:0.7em;}`,
		`${scope} details[open] > summary .pb-faq-chevron{transform:rotate(90deg);}`,
		`${scope} .pb-faq-pm{position:relative;width:0.85em;height:0.85em;}`,
		`${scope} .pb-faq-pm span{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-weight:600;line-height:1;transition:opacity 0.18s ease,transform 0.18s var(--pb-faq-ease);}`,
		`${scope} details:not([open]) > summary .pb-faq-pm-open{opacity:0;transform:rotate(90deg) scale(0.6);pointer-events:none;}`,
		`${scope} details[open] > summary .pb-faq-pm-collapsed{opacity:0;transform:rotate(-90deg) scale(0.6);pointer-events:none;}`,
		/* Entrance when opened — works with native <details> (UA hides closed content). */
		`@keyframes pb-faq-answer-in-${faqDesignScopeId(nodeId)}{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:none}}`,
		`${scope} details[open] > .pb-faq-answer .pb-faq-answer-body{animation:pb-faq-answer-in-${faqDesignScopeId(nodeId)} 0.28s var(--pb-faq-ease) both;}`,
		`${scope} .pb-faq-answer{min-width:0;}`,
	];

	if (design.preset === "divided") {
		lines.push(
			`${scope}.pb-faq-preset-divided details:last-child{border-bottom:none !important;}`,
			`${scope}.pb-faq-preset-divided details[open] > summary{color:var(--tt-color-primary,#0ea5e9);}`
		);
	} else if (design.preset === "cards") {
		lines.push(
			`${scope}.pb-faq-preset-cards details:hover{box-shadow:0 8px 28px color-mix(in srgb,var(--tt-color-fg,#0f172a) 12%,transparent);}`,
			`${scope}.pb-faq-preset-cards details[open]{box-shadow:0 10px 32px color-mix(in srgb,var(--tt-color-fg,#0f172a) 14%,transparent);}`
		);
	} else if (design.preset === "soft") {
		lines.push(
			`${scope}.pb-faq-preset-soft details[open]{background-color:color-mix(in srgb,var(--tt-color-primary,#0ea5e9) 8%,var(--tt-color-muted,#f1f5f9));}`,
			`${scope}.pb-faq-preset-soft .pb-faq-icon{background-color:color-mix(in srgb,var(--tt-color-fg,#0f172a) 6%,transparent);}`
		);
	} else if (design.preset === "accent") {
		lines.push(
			`${scope}.pb-faq-preset-accent details[open]{background-color:color-mix(in srgb,var(--tt-color-primary,#0ea5e9) 10%,var(--tt-color-bg,#fff));}`,
			`${scope}.pb-faq-preset-accent .pb-faq-icon{color:var(--tt-color-primary,#0ea5e9);opacity:1;}`
		);
	} else {
		lines.push(
			`${scope}.pb-faq-preset-outlined details[open]{border-color:color-mix(in srgb,var(--tt-color-primary,#0ea5e9) 45%,transparent);}`,
			`${scope}.pb-faq-preset-outlined .pb-faq-icon{background-color:color-mix(in srgb,var(--tt-color-fg,#0f172a) 5%,transparent);}`
		);
	}

	if (qSize) {
		// Beat Theme Manager tag rules (same selector strength as heading Main Options).
		lines.push(
			`${pbNodeBeatThemeDescendantSelector(nodeId, ".pb-faq-q")}{font-size:${qSize} !important;}`
		);
	}
	if (aSize) {
		lines.push(
			`${pbNodeBeatThemeDescendantSelector(nodeId, ".pb-faq-answer-body")}{font-size:${aSize} !important;}`
		);
	}
	/* Always beat accidental wrapper / UA Times on answer copy. */
	lines.push(
		`${pbNodeBeatThemeDescendantSelector(nodeId, ".pb-faq-answer-body")}{font-family:var(--tt-font-sans,system-ui,sans-serif) !important;}`
	);

	return lines.join("");
}

/** Apply a template: preset id + matching default styles / chrome. */
export function applyFaqDesignPreset(
	preset: FaqDesignPreset,
	currentDesign?: FaqDesignSettings | null
): {
	design: FaqDesignSettings;
	defaultAnswerStyles: ElementStyles;
	defaultItemStyles: ElementStyles;
	defaultQuestionStyles: ElementStyles;
	iconPosition: "left" | "right";
	itemGap: string;
	triggerIcon: "chevron" | "plus_minus" | "none";
} {
	const bundle = FAQ_PRESET_STYLE_BUNDLES[preset];
	const prev = currentDesign ?? {};
	return {
		design: {
			...prev,
			preset,
		},
		defaultItemStyles: { ...bundle.defaultItemStyles },
		defaultQuestionStyles: {
			...bundle.defaultQuestionStyles,
			// Preserve author font sizes across template switches when set on design.
			...(prev.questionFontSize?.trim()
				? { fontSize: prev.questionFontSize.trim() }
				: {}),
		},
		defaultAnswerStyles: {
			...bundle.defaultAnswerStyles,
			...(prev.answerFontSize?.trim()
				? { fontSize: prev.answerFontSize.trim() }
				: {}),
		},
		itemGap: bundle.itemGap,
		triggerIcon: bundle.triggerIcon,
		iconPosition: bundle.iconPosition,
	};
}
